import React, { useEffect, useState, useRef, useContext } from "react";
import {
  FaLaptopCode,
  FaShoppingCart,
  FaGlobe,
  FaArrowRight,
  FaPhone,
  FaUsers,
  FaBriefcase,
  FaCalendarAlt,
  FaChartLine,
  FaBolt,
  FaCogs,
  FaCarAlt,
  FaWater,
  FaWeightHanging,
  FaPlay,
  FaPause,
  FaExpand,
  FaVolumeUp,
  FaVolumeMute,
  FaBackward,
  FaForward
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useTranslation, Trans } from 'react-i18next';
import { useSpring, animated, config } from 'react-spring';
import ReactPlayer from "react-player";
import { VideoLoadingContext } from '../App';
import { NavbarAnimationContext } from '../components/Navbar';
import IntroAnimation from "../components/IntroAnimation";
import { unveilStorage } from '../utils/localStorage';
import SEO from "../components/SEO";

const Home = () => {
  const { t, i18n } = useTranslation();
  const { videosLoading, setVideosLoading } = useContext(VideoLoadingContext);
  const { navbarShouldAnimate, setNavbarShouldAnimate } = useContext(NavbarAnimationContext);
  const [videoLoadCount, setVideoLoadCount] = useState(0);
  const totalVideos = 2; // Number of videos on this page
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialAnimationComplete, setInitialAnimationComplete] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  // Hero hover interplay: 'eu' expands the EU flag panel, 'video' expands the prototype video
  const [heroFocus, setHeroFocus] = useState(null);

  // Video player state - First video
  const videoRef1 = useRef(null);
  const progressRef1 = useRef(null);
  const [videoPlaying1, setVideoPlaying1] = useState(false);
  const [videoLoaded1, setVideoLoaded1] = useState(false);
  const [videoDuration1, setVideoDuration1] = useState(0);
  const [currentTime1, setCurrentTime1] = useState(0);
  const [volume1, setVolume1] = useState(1);
  const [muted1, setMuted1] = useState(true); // Start muted

  // Video player state - Second video
  const videoRef2 = useRef(null);
  const progressRef2 = useRef(null);
  const [videoPlaying2, setVideoPlaying2] = useState(false);
  const [videoLoaded2, setVideoLoaded2] = useState(false);
  const [videoDuration2, setVideoDuration2] = useState(0);
  const [currentTime2, setCurrentTime2] = useState(0);
  const [volume2, setVolume2] = useState(1);
  const [muted2, setMuted2] = useState(true); // Start muted

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if the user has already seen the unveil animation
  useEffect(() => {
    const checkUnveilStatus = () => {
      const hasUnveiled = unveilStorage.get();
      setShowIntro(!hasUnveiled);
      setIsLoading(false);
    };

    checkUnveilStatus();
  }, []);

  // Immediately show navbar when component mounts
  useEffect(() => {
    // Animate the navbar as soon as the Home component loads
    setNavbarShouldAnimate(true);
  }, [setNavbarShouldAnimate]);

  // Format time to mm:ss - kept for compatibility
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Simplified video loading handler
  const handleVideoLoad = () => {
    setVideoLoadCount(prev => {
      const newCount = prev + 1;
      if (!showIntro && newCount >= totalVideos) {
        setVideosLoading(false);
      }
      return newCount;
    });

    // Set videos as loaded
    setVideoLoaded1(true);
    setVideoLoaded2(true);
  };

  const serviceCardKeys = [
    'customWebApp',
    'ecommerce',
    'businessWebsite',
    'portfolio',
    'booking',
    'consulting'
  ];

  const iconColor = "text-[#212529]";
  const serviceIcons = {
    customWebApp: <FaLaptopCode className={`${iconColor} text-5xl mx-auto mb-4`} />,
    ecommerce: <FaShoppingCart className={`${iconColor} text-5xl mx-auto mb-4`} />,
    businessWebsite: <FaGlobe className={`${iconColor} text-5xl mx-auto mb-4`} />,
    portfolio: <FaBriefcase className={`${iconColor} text-5xl mx-auto mb-4`} />,
    booking: <FaCalendarAlt className={`${iconColor} text-5xl mx-auto mb-4`} />,
    consulting: <FaChartLine className={`${iconColor} text-5xl mx-auto mb-4`} />
  };

  const primaryTextColor = "text-[#212529]";
  const secondaryTextColor = "text-[#212529]/80";
  const tertiaryTextColor = "text-[#212529]/60";

  const handleUnveil = () => {
    // Update the unveil status using the new localStorage utility
    unveilStorage.set(true);

    // Dispatch a custom event to notify App component about the unveil
    window.dispatchEvent(new CustomEvent('unveilStatusChanged', { detail: { hasUnveiled: true } }));

    // No need to set navbar animation here as it's already shown
    // Just hide the intro and show main content
    setTimeout(() => {
      setShowIntro(false);
      setIsInitialLoad(true); // Reset for the square-to-triangle animation
      setInitialAnimationComplete(false); // Reset animation completion state

      if (videoLoadCount >= totalVideos) {
        setVideosLoading(false);
      }
    }, 300); // Reduced delay since navbar is already visible
  };

  // Show loading or the appropriate content
  if (isLoading) {
    return null; // Or a minimal loading indicator
  }

  // The intro is rendered as a fixed overlay ON TOP of the page content instead of
  // replacing it, so the homepage text is always present in the DOM for search
  // engines and AI crawlers (which arrive with clean storage and would otherwise
  // only ever see the intro screen).
  return (
    <div className="relative font-sans overflow-hidden min-h-screen">
      {showIntro && <IntroAnimation onUnveil={handleUnveil} />}
      <SEO 
        title="Axialis - Yokeless Axial Flux Electric Motors"
        description="Axialis develops next-generation yokeless axial flux electric motors with integrated water cooling for automotive applications. Experience groundbreaking power density and efficiency."
        keywords="Axialis, yokeless axial flux, electric motor, automotive, EV, water cooling, high power density, efficiency, electric drive systems"
        path="/"
      />
      {/* Hero Section */}
      <section
        className="relative min-h-[calc(100vh-4rem)] py-16 md:py-24 overflow-hidden"
        style={{
          zIndex: 10,
          transform: `translateY(${scrollY * 0.1}px)`
        }}
      >
        <div className="container mx-auto flex flex-col md:flex-row md:items-stretch gap-6 px-4 relative z-10">
          {/* EU flag panel — far left; hovering it settles it open and reveals the full project text.
              On mobile (no hover) it stacks below the motor card with the text always visible. */}
          <div
            onMouseEnter={() => setHeroFocus('eu')}
            className={`order-2 md:order-1 w-full flex flex-col items-center justify-center backdrop-blur-sm bg-white/50 border border-white/40 rounded-xl shadow-lg p-4 md:p-6 overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] flex-shrink-0 ${heroFocus === 'eu' ? 'md:w-[24rem] lg:w-[32rem] xl:w-[40rem]' : heroFocus === 'video' ? 'md:w-[12%] md:opacity-70' : 'md:w-[22%]'}`}
          >
            <img
              src="/images/eu-funded.png?v=5"
              alt="Funded by the European Union - NextGenerationEU"
              className={`h-auto object-contain transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${heroFocus === 'eu' ? 'w-2/5' : 'w-2/3 md:w-full'}`}
            />
            {/* Curtain wrapper: animates height/margin only. The inner block below is laid out at its
                FINAL fixed width the whole time, so text never re-wraps while the panel resizes. */}
            <div className={`w-full overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${heroFocus === 'eu' ? 'max-h-[90rem] mt-5' : 'max-h-[90rem] mt-5 md:max-h-0 md:mt-0'}`}>
              <div className={`mx-auto w-full md:w-[20rem] lg:w-[28rem] xl:w-[36rem] transition-opacity ${heroFocus === 'eu' ? 'opacity-100 duration-500 delay-200' : 'opacity-100 md:opacity-0 duration-200 delay-0'}`}>
              <p className="text-[#212529] font-medium text-base md:text-lg text-center mb-4 leading-snug">
                The development of new axial flux electric motor technology
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2 text-xs mb-4">
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">Coordinator</span><span className="font-semibold text-right text-[#212529]">Axialis j.d.o.o.</span></p>
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">Total Project Value</span><span className="font-semibold text-right text-[#212529]">360.745,01 euro</span></p>
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">Total Eligible Costs</span><span className="font-semibold text-right text-[#212529]">358.714,34 euro</span></p>
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">EU Contribution to Project Funding</span><span className="font-semibold text-right text-[#212529]">198.874,17 euro</span></p>
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">Implementation Period</span><span className="font-semibold text-right text-[#212529]">01.01.2024. – 30.06.2026.</span></p>
                <p className="flex justify-between gap-3 border-b border-[#212529]/10 pb-1"><span className="text-[#212529]/60">Project Manager and Contact Person</span><span className="font-semibold text-right text-[#212529]">Marko Đula, director, <a href="mailto:mdjula00@gmail.com" className="text-primary hover:underline">mdjula00@gmail.com</a></span></p>
              </div>
              <div className="space-y-3 text-sm text-[#212529]/80 leading-relaxed mb-4 text-left">
                <p>
                  The company Axialis Ltd. commenced the implementation of the project: Development of New Axial Flux Electric Motor Technology of a spin-off venture Axialis j.d.o.o., on January 1, 2024. This project is being carried out in accordance with the Grant Agreement for projects funded by the Recovery and Resilience Facility, signed with the Ministry of Science and Education and the Croatian Agency for Small Business, Innovation, and Investment. The project is co-financed by the European Union through the Recovery and Resilience Facility instrument.
                </p>
                <p>
                  The project "Development of New Axial Flux Electric Motor Technology by Axialis Ltd." aims to advance the research of the project team for the development, testing, and protection of a prototype axial flux motor with innovative cooling technology and significantly improved geometry optimization. This will enable broader applications of axial flux motors. The anticipated results will enhance the performance of electric motors and electric vehicles. The project activities, including additional training, will increase the company's capacity, improve the competencies of young researchers, and strengthen the link between the scientific and private sectors.
                </p>
              </div>
              <p className="text-sm font-medium text-[#212529] mb-2 text-left">The project consists of the following activities:</p>
              <ul className="list-disc list-outside pl-5 space-y-1.5 text-sm text-[#212529]/80 text-left">
                <li>Designing and testing the optimized geometry of the axial flux electric motor</li>
                <li>Designing, testing, and patenting a new cooling system for the axial flux electric motor</li>
                <li>Business development and training for young researchers</li>
                <li>Project management</li>
                <li>Promotion and visibility</li>
              </ul>
              </div>
            </div>
          </div>

          {/* Combined motor component — intro text + prototype video in one card */}
          <div
            onMouseEnter={() => setHeroFocus('video')}
            className="order-1 md:order-2 flex-1 min-w-0 backdrop-blur-sm bg-white/40 border border-white/40 rounded-xl shadow-xl p-6 md:p-8 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          >
            <div className="flex flex-col md:flex-row items-center gap-8 h-full">
              <div className="w-full md:w-7/12 text-left">
                <HeroContent />
              </div>
              <div className="w-full md:w-5/12">
                <VideoAnimation
                  videoRef={videoRef1}
                  videoPlaying={videoPlaying1}
                  setVideoPlaying={setVideoPlaying1}
                  muted={muted1}
                  volume={volume1}
                  handleVideoLoad={handleVideoLoad}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner / Simulation Tools Section */}
      <section className="pt-8 pb-24 relative overflow-hidden" style={{
        zIndex: 10,
        transform: `translateY(${scrollY * 0.03}px)` // Slight parallax, softer than the EU section above
      }}>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-center items-center">
            <PartnerLogoAnimation />
          </div>
        </div>
      </section>
    </div>
  );
};

// Partner logo animation component - Ansys "Simulation powered by" section
const PartnerLogoAnimation = () => {
  // Debug render log with timestamp
  console.log(`[${new Date().toISOString()}] PartnerLogoAnimation rendered`);

  // Intersection observer to trigger animation when scrolled into view
  const [ref, inView] = useInView({
    triggerOnce: true, // Only animate once
    threshold: 0.3, // Trigger when 30% visible
    rootMargin: '0px 0px -30px 0px' // Slight offset from bottom
  });

  // Fade-in + slight scale animation, matching FundingAnimation style
  const animation = useSpring({
    from: {
      opacity: 0, // Start invisible
      transform: 'translateY(15px) scale(0.97)', // Start slightly below and smaller
      filter: 'blur(2px)' // Start with slight blur
    },
    to: {
      opacity: inView ? 1 : 0, // Fade in when in view
      transform: inView ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.97)', // Slide up to position
      filter: inView ? 'blur(0px)' : 'blur(2px)' // Clear blur when in view
    },
    config: {
      mass: 1.8, // Slightly lighter than FundingAnimation for snappier feel
      tension: 90, // Smooth spring tension
      friction: 24 // Smooth friction
    },
    delay: 200 // Slight delay for staggered feel after EU section
  });

  return (
    <div ref={ref} className="w-full flex flex-col items-center justify-center">
      <animated.div style={animation} className="flex flex-col items-center">
        {/* Subtle label text above the logo */}
        <p className="text-[#212529]/50 text-xs md:text-sm font-light tracking-widest uppercase mb-4">
          Simulation powered by
        </p>

        {/* Ansys logo - clickable, links to ansys.com/startups */}
        <a
          href="https://www.ansys.com/startup-program"
          target="_blank" // Open in new tab
          rel="noopener noreferrer" // Security: prevent reverse tabnapping
          className="group inline-block transition-all duration-300"
          aria-label="Ansys - Simulation Software for Startups" // Accessibility label
        >
          {/* Glassmorphism card wrapping the logo */}
          <img
            src="/images/ansys_logo.webp" // Ansys partner logo
            alt="Ansys - Simulation Software" // Alt text for accessibility & SEO
            style={{ width: '50%', maxWidth: '750px' }} // Just a bit smaller than EU logo (64.8% / 972px)
            className="h-auto mx-auto object-contain group-hover:scale-[1.02] transition-transform duration-300" // Subtle hover scale, no card wrapper
            loading="lazy" // Lazy load since it's below the fold
          />
        </a>
      </animated.div>
    </div>
  );
};

// Hero content with animations
const HeroContent = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const titleAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(-20px)' },
    config: { mass: 1, tension: 180, friction: 25 },
    delay: 200
  });

  const lineAnimation = useSpring({
    from: { width: '0px', opacity: 0 },
    to: { width: inView ? '64px' : '0px', opacity: inView ? 1 : 0 },
    config: { mass: 1, tension: 180, friction: 25 },
    delay: 600
  });

  const cardAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)' },
    config: { mass: 1, tension: 150, friction: 25 },
    delay: 800
  });

  const featuresAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)' },
    config: { mass: 1, tension: 150, friction: 25 },
    delay: 1000
  });

  const buttonAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)' },
    config: { mass: 1, tension: 150, friction: 25 },
    delay: 1200
  });

  return (
    <div ref={ref}>
      <animated.h1 style={titleAnimation} className="font-display text-4xl md:text-6xl font-bold text-[#212529] mb-6">
        <span className="text-primary">AXI</span>ALIS
      </animated.h1>

      <animated.div style={lineAnimation} className="h-0.5 bg-primary mb-8"></animated.div>

      <animated.div style={cardAnimation} className="mb-8">
        <p className="text-base md:text-lg text-[#212529]/90 mb-5 leading-relaxed font-light">
          Axialis specializes in the development of yokeless axial flux electric motors designed for a wide range of high-performance applications.
        </p>
        <p className="text-base md:text-lg text-[#212529]/90 mb-5 leading-relaxed font-light">
          Our work focuses on pushing the boundaries of axial flux technology through innovative, liquid-cooled motor designs.
        </p>
        <p className="text-base md:text-lg text-[#212529]/90 mb-0 leading-relaxed font-light">
          With a focus on power density and energy efficiency, our motors contribute to the ongoing evolution of electric drive systems.
        </p>
      </animated.div>

      <animated.div style={buttonAnimation}>
        <Link
          to="/explore-motor"
          className="relative inline-flex items-center text-lg font-medium text-primary group py-3 px-6 border border-white/40 rounded-lg backdrop-blur-sm bg-white/50 shadow-md hover:bg-primary/10 hover:shadow-lg transition-all duration-300"
        >
          <span>Explore the Motor</span>
          <FaArrowRight className="ml-3 transform group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </animated.div>
    </div>
  );
};

// Video with animation component
const VideoAnimation = ({ videoRef, videoPlaying, setVideoPlaying, muted, volume, handleVideoLoad }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const videoAnimation = useSpring({
    from: {
      opacity: 0,
      transform: 'translateX(50px)',
    },
    to: {
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateX(0)' : 'translateX(50px)',
    },
    config: {
      mass: 1.5,
      tension: 140,
      friction: 35
    },
    delay: 800
  });

  return (
    <div ref={ref} className="w-full h-full flex items-center justify-center">
      <animated.div
        style={videoAnimation}
        className="rounded-xl overflow-hidden shadow-xl border border-gray-200/50 w-full max-w-md mx-auto"
      >
        <div className="relative w-full" style={{ paddingTop: '140%' }}>
          <ReactPlayer
            ref={videoRef}
            url="/axialisvideo/VID-1.mp4"
            width="100%"
            height="100%"
            playing={videoPlaying}
            controls={true}
            light={false}
            loop={false}
            muted={muted}
            volume={volume}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '0.75rem',
              overflow: 'hidden',
              objectFit: 'contain',
              backgroundColor: '#f8f9fa'
            }}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true
                }
              }
            }}
            onReady={() => handleVideoLoad()}
            onPlay={() => setVideoPlaying(true)}
            onPause={() => setVideoPlaying(false)}
            onEnded={() => setVideoPlaying(false)}
          />

          {/* Simple play button overlay */}
          <div
            className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-all duration-300 ${videoPlaying ? 'opacity-0' : 'opacity-100'} z-10 pointer-events-none`}
          >
            <div
              className="w-16 h-16 rounded-full bg-primary/80 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-primary hover:scale-105 transition-all duration-300 shadow-lg pointer-events-auto"
              onClick={() => setVideoPlaying(!videoPlaying)}
            >
              <FaPlay className="text-white text-xl ml-1" />
            </div>
          </div>
        </div>
      </animated.div>
    </div>
  );
};

export default Home;